import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import FeeStructure from './FeeStructure';
import AssignFees from './AssignFees';
import FeeCollection from './FeeCollection';
import StudentFeesLookup from './StudentFeesLookup';
import SalaryPayment from './SalaryPayment';
import ExpenseManagement from './ExpenseManagement';
import PaymentConfig from './PaymentConfig';

interface FinanceModuleProps {
  domain: string;
  onBack: () => void;
}

const FinanceModule: React.FC<FinanceModuleProps> = ({ domain, onBack }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<string>('fee-structure');

  // Update active tab based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/assign-fees')) setActiveTab('assign-fees');
    else if (path.includes('/fee-collection')) setActiveTab('fee-collection');
    else if (path.includes('/student-fees')) setActiveTab('student-fees');
    else if (path.includes('/salary-payment')) setActiveTab('salary-payment');
    else if (path.includes('/expenses')) setActiveTab('expenses');
    else if (path.includes('/payment-config')) setActiveTab('payment-config');
    else setActiveTab('fee-structure');
  }, [location.pathname]);

  return (
    <div className="min-vh-100 bg-light">
      {/* Header */}
      <div className="bg-primary-custom text-white p-3 shadow-sm">
        <div className="container-fluid d-flex justify-content-between align-items-center">
          <div>
            <h4 className="mb-0">
              <i className="bi bi-currency-dollar me-2"></i>
              Finance Management
            </h4>
            <small>Fee structures, assignments, collection & salary payments</small>
          </div>
          <button className="btn btn-light btn-sm" onClick={onBack}>
            <i className="bi bi-arrow-left me-2"></i>
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white shadow-sm sticky-top" style={{ top: 0, zIndex: 100 }}>
        <div className="container-fluid">
          <ul className="nav nav-tabs border-0">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'fee-structure' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/finance')}
              >
                <i className="bi bi-receipt me-2"></i>
                Fee Structure
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'assign-fees' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/finance/assign-fees')}
              >
                <i className="bi bi-person-plus-fill me-2"></i>
                Assign Fees
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'fee-collection' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/finance/fee-collection')}
              >
                <i className="bi bi-cash-coin me-2"></i>
                Fee Collection
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'student-fees' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/finance/student-fees')}
              >
                <i className="bi bi-search me-2"></i>
                Student Fees
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'salary-payment' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/finance/salary-payment')}
              >
                <i className="bi bi-cash-stack me-2"></i>
                Salary Payment
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'expenses' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/finance/expenses')}
              >
                <i className="bi bi-wallet2 me-2"></i>
                Expense Management
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'payment-config' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/finance/payment-config')}
              >
                <i className="bi bi-credit-card me-2"></i>
                Payment Config
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Content Area */}
      <div className="container-fluid py-4">
        <Routes>
          <Route index element={<FeeStructure domain={domain} />} />
          <Route path="assign-fees" element={<AssignFees domain={domain} />} />
          <Route path="fee-collection" element={<FeeCollection domain={domain} />} />
          <Route path="student-fees" element={<StudentFeesLookup domain={domain} />} />
          <Route path="salary-payment" element={<SalaryPayment domain={domain} />} />
          <Route path="expenses" element={<ExpenseManagement domain={domain} />} />
          <Route path="payment-config" element={<PaymentConfig domain={domain} />} />
        </Routes>
      </div>

      {/* Information Footer */}
      <div className="container-fluid py-3">
        <div className="alert alert-info">
          <h6 className="alert-heading">
            <i className="bi bi-info-circle me-2"></i>
            Finance Module Features
          </h6>
          <hr />
          <div className="row">
            <div className="col-md-2">
              <strong>Fee Structure:</strong>
              <ul className="small mb-0 mt-2">
                <li>Create fee templates</li>
                <li>Multiple installments</li>
              </ul>
            </div>
            <div className="col-md-2">
              <strong>Assign Fees:</strong>
              <ul className="small mb-0 mt-2">
                <li>Auto-assign by class</li>
                <li>Bulk assignment</li>
              </ul>
            </div>
            <div className="col-md-2">
              <strong>Fee Collection:</strong>
              <ul className="small mb-0 mt-2">
                <li>Track payments</li>
                <li>Balance tracking</li>
              </ul>
            </div>
            <div className="col-md-2">
              <strong>Student Fees:</strong>
              <ul className="small mb-0 mt-2">
                <li>Lookup by student ID</li>
                <li>View fee details</li>
              </ul>
            </div>
            <div className="col-md-2">
              <strong>Salary Payment:</strong>
              <ul className="small mb-0 mt-2">
                <li>Teacher salaries</li>
                <li>Payment history</li>
              </ul>
            </div>
            <div className="col-md-2">
              <strong>Expenses:</strong>
              <ul className="small mb-0 mt-2">
                <li>Track expenses</li>
                <li>Approval workflow</li>
              </ul>
            </div>
            <div className="col-md-2">
              <strong>Payment Config:</strong>
              <ul className="small mb-0 mt-2">
                <li>Bank account setup</li>
                <li>UPI configuration</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinanceModule;
